import type { ValidationError } from '../errors/validation-error'
import type { ExpandedRowProps } from '../types'

import { Button, Checkbox, Col, Form, Input, Row, Select, Space, Typography } from 'antd'
import cx from 'classnames'
import dayjs from 'dayjs'
import { useState } from 'react'

import { mapper as pushTypeNameMapper, PushType } from '../../../domain/push-type.enum'
import { currency } from '../../premium/formatters'

import { MaskedInput } from './masked-input'

export function ExpandedRow({
  row,
  form,
  isSubmitting,
  error,
  onSave,
  onCancel
}: ExpandedRowProps): JSX.Element {
  const validationError = error as Partial<ValidationError> | undefined
  const fundingType = Form.useWatch('funding_type', form)
  const [emails, setEmails] = useState<Array<{ email: string; name: string }>>([])

  return (
    <>
      <Form
        form={form}
        layout='vertical'
        className='p-6'
        colon={false}
        requiredMark={false}
        labelCol={{}}
        wrapperCol={{
          className: '-mt-1.5'
        }}
      >
        <Row gutter={32} style={{ gap: 24 }}>
          <Col span={10} className='border p-4 rounded-lg'>
            <Typography.Title
              level={5}
              className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray mb-2'
            >
              General information
            </Typography.Title>
            <Row gutter={24}>
              <Col span={12}>
                <div className='flex flex-col gap-4 justify-start items-start bg-lucent-light-gray rounded-xl text-lucent-text p-6 max-w-xs h-full'>
                  <div className='flex flex-col w-full'>
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                      Account Payables
                    </span>

                    <div
                      className={cx(
                        'flex flex-row justify-between items-center gap-8 p-2 rounded -mx-2 -mt-2',
                        row?.current_ar !== row?.open_ar && 'bg-purple-100'
                      )}
                    >
                      <span className='font-lato text-sm font-semibold text-lucent-text'>
                        Current
                      </span>

                      <span className='font-lato text-sm font-semibold text-lucent-text'>
                        {currency(row?.current_ar ?? 0)}
                      </span>
                    </div>

                    <div
                      className={cx(
                        'flex flex-row justify-between items-center gap-16 p-2 rounded -m-2',
                        row?.current_ar !== row?.open_ar && 'bg-purple-100'
                      )}
                    >
                      <span className='font-lato text-sm font-semibold text-lucent-text'>Open</span>

                      <span className='font-lato text-sm font-semibold text-lucent-text'>
                        {currency(row?.open_ar ?? 0)}
                      </span>
                    </div>
                  </div>

                  <div className='flex flex-col'>
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                      Vendor ID
                    </span>

                    <span className='font-lato text-sm text-lucent-text'>{row?.vendor_id}</span>
                  </div>

                  <div className='flex flex-col'>
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                      Vendor Name
                    </span>

                    <span className='font-lato text-sm text-lucent-text'>{row?.name}</span>
                  </div>

                  <div className='flex flex-col'>
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                      Location
                    </span>

                    <span className='font-lato text-sm text-lucent-text'>
                      {row?.location.short_name}
                    </span>
                  </div>

                  <div className='flex flex-col'>
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                      Federal tax id
                    </span>

                    <span className='font-lato text-sm text-lucent-text'>{row?.federal_id}</span>
                  </div>

                  <div className='flex flex-col'>
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                      ID
                    </span>

                    <span className='font-lato text-xs text-lucent-text'>{row?.id}</span>
                  </div>

                  <div className='flex flex-col'>
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray pb-1'>
                      Last Synced
                    </span>

                    <span className='font-lato text-xs text-lucent-text'>
                      {`${dayjs(row?.updated_at).format('MM/DD/YYYY')} at ${dayjs(row?.updated_at).format('HH:mm')}`}
                    </span>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <Form.Item
                  name='billing_address'
                  className='mb-0'
                  label={
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                      Remit-to address
                    </span>
                  }
                >
                  <p className='leading-normal pb-[13px]'>
                    {row?.address_1 ?? <span className='text-gray-400'>Not set</span>}
                  </p>
                </Form.Item>

                <Form.Item
                  name='address2'
                  className='mb-0'
                  label={
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                      Remit-to address 2
                    </span>
                  }
                >
                  <p className='leading-normal pb-[13px]'>
                    {row?.address_2 ?? <span className='text-gray-400'>Not set</span>}
                  </p>
                </Form.Item>

                <Form.Item
                  name='city'
                  className='mb-0'
                  label={
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                      City
                    </span>
                  }
                >
                  <p className='leading-normal pb-[13px]'>
                    {row?.city ?? <span className='text-gray-400'>Not set</span>}
                  </p>
                </Form.Item>

                <Form.Item
                  name='state'
                  className='mb-0'
                  label={
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                      State
                    </span>
                  }
                >
                  <p className='leading-normal pb-[13px]'>
                    {row?.location.short_name ?? <span className='text-gray-400'>Not set</span>}
                  </p>
                </Form.Item>

                <Form.Item
                  name='zip'
                  className='mb-0'
                  label={
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                      ZIP
                    </span>
                  }
                >
                  <p className='leading-normal pb-[13px]'>
                    {row?.zip ?? <span className='text-gray-400'>Not set</span>}
                  </p>
                </Form.Item>

                <div className='h-2' />

                <Form.Item name='is_active' valuePropName='checked'>
                  <Checkbox>Active</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col span={13} className='[&_.ant-form-item]:mb-2'>
            <Row gutter={32} className='mb-2' style={{ gap: 24 }}>
              <Col className='border p-4 rounded-lg mb-3' span={16}>
                <Row className='mt-10'>
                  <Typography.Title
                    level={5}
                    className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'
                  >
                    Vendor account
                  </Typography.Title>
                </Row>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name='routing_number'
                      label={
                        <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                          Vendor Routing Number
                        </span>
                      }
                      validateStatus={validationError?.issues?.routing_number ? 'error' : undefined}
                      help={validationError?.issues?.routing_number}
                      required={fundingType === PushType.Pull}
                    >
                      <MaskedInput
                        mask='999999999'
                        size='middle'
                        className='w-full rounded-md'
                        placeholder={fundingType === PushType.Pull ? 'E.g. 104000854' : '—'}
                        disabled={false}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item
                      name='account_number'
                      label={
                        <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                          Vendor Account Number
                        </span>
                      }
                      validateStatus={validationError?.issues?.account_number ? 'error' : undefined}
                      help={validationError?.issues?.account_number}
                      required={fundingType === PushType.Pull}
                    >
                      <Input
                        size='middle'
                        className='w-full rounded-md'
                        placeholder={fundingType === PushType.Pull ? 'E.g. 702144500' : '—'}
                        disabled={false}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col className='[&_.ant-form-item]:mb-2.5 flex-grow border p-4 rounded-lg'>
                <Typography.Title
                  level={5}
                  className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray mb-2'
                >
                  Funding Information
                </Typography.Title>
                <Form.Item
                  name='funding_type'
                  label={
                    <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                      Funding Type
                    </span>
                  }
                  validateStatus={validationError?.issues?.funding_type ? 'error' : undefined}
                  help={validationError?.issues?.funding_type}
                  tooltip={
                    <div className='flex flex-col gap-2'>
                      <p>
                        <strong>Push</strong> is when groups send us money to fund their claims via
                        ACH, wire or physical check.
                      </p>
                    </div>
                  }
                  required
                >
                  <Select
                    size='middle'
                    className='w-full rounded-md'
                    placeholder='Select Funding Type'
                    allowClear
                    options={[
                      {
                        label: pushTypeNameMapper[PushType.Push],
                        value: PushType.Push
                      },
                      {
                        label: pushTypeNameMapper[PushType.Check],
                        value: PushType.Check
                      }
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={32}>
              <Col span={24} className='border p-4 rounded-md'>
                <Row>
                  <Col span={20}>
                    <Typography.Title
                      level={5}
                      className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray mb-2'
                    >
                      Vendor email management
                    </Typography.Title>
                  </Col>
                  <Col>
                    <Button
                      onClick={() => {
                        setEmails(e => [...e, { email: '', name: '' }])
                      }}
                    >
                      + Add Recipient
                    </Button>
                  </Col>
                </Row>
                {emails.map((email, idx) => (
                  <Row gutter={16} key={idx}>
                    <Col span={12}>
                      <Form.Item
                        name={`email.[${idx}].email`}
                        label={
                          <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                            Email
                          </span>
                        }
                      >
                        <Input size='middle' className='w-full rounded-md' placeholder='Email' />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={`email.[${idx}].name`}
                        label={
                          <span className='font-lato tracking-wide uppercase text-[9.5px] font-semibold text-lucent-gray'>
                            Name
                          </span>
                        }
                      >
                        <Input size='middle' className='w-full rounded-md' placeholder='Name' />
                      </Form.Item>
                    </Col>
                  </Row>
                ))}
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className='pt-6'>
          <Col span={24} className='flex justify-end'>
            <Space>
              <Button
                size='middle'
                type='link'
                htmlType='reset'
                className='rounded-md'
                onClick={event => {
                  event.preventDefault()
                  onCancel()
                }}
              >
                Cancel
              </Button>

              <Button
                size='middle'
                type='primary'
                htmlType='submit'
                loading={isSubmitting}
                onClick={event => {
                  event.preventDefault()
                  onSave()
                }}
              >
                Save
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </>
  )
}
